// src/components/includes/LocationSearch.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useGoogleMaps } from "../../hooks/useGoogleMaps";

const LocationSearch = ({
  onPlaceSelect,
  defaultAddress,
  defaultAddressTitle,
  searchLocationTitle,
}) => {
  const mapsLoaded = useGoogleMaps();
  const [isEditing, setIsEditing] = useState(!defaultAddress);

  const containerRef = useRef(null);
  const autocompleteRef = useRef(null);

  // 🧩 Re-sync state if defaultAddress changes later (e.g., after fetch)
  useEffect(() => {
    if (defaultAddress) {
      setIsEditing(false);
    }
  }, [defaultAddress]);

  const extract = useCallback((place) => {
    const comp = place.addressComponents || [];

    const get = (types) =>
      comp.find((c) => c.types.some((t) => types.includes(t)))?.longText ?? "";

    const streetNumber = get(["street_number"]);
    const route = get(["route"]);
    const neighborhood = get(["neighborhood"]);
    const sublocality1 = get(["sublocality_level_1"]);
    const sublocality = get(["sublocality"]);
    const locality = get(["locality", "administrative_area_level_2"]);
    const admin1 = get(["administrative_area_level_1"]);
    const country = get(["country"]);
    const postalCode = get(["postal_code"]);

    const parts = [streetNumber, route, neighborhood, sublocality1].filter(
      Boolean
    );
    const address =
      parts.length > 0
        ? parts.join(", ")
        : sublocality || place.formattedAddress?.split(",")[0].trim() || "";

    // Add lat/lng from place.location
    const lat = String(place.location?.lat());
    const lng = String(place.location?.lng());

    return {
      address,
      country,
      city: locality,
      state: admin1,
      postalCode,
      lat,
      lng,
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!mapsLoaded) return; // ⬅ Wait until Google Maps is ready
    if (!isEditing) return;
    // if (!window.google?.maps) return;
    // Enhanced check for importLibrary
    // if (!window.google?.maps?.importLibrary) {
    //   console.warn("Google Maps importLibrary not ready yet");
    //   return;
    // }

    let autocomplete = null;

    const init = async (retryCount = 0) => {
      try {
        //  Implement Retry Logic
        if (!window.google?.maps?.importLibrary) {
          if (retryCount < 5) {
            // Increase retry count
            setTimeout(() => init(retryCount + 1), 1000); // Increase delay
            return;
          }
          throw new Error("Google Maps API not available after retries");
        }

        const { PlaceAutocompleteElement } =
          await window.google.maps.importLibrary("places");

        autocomplete = new PlaceAutocompleteElement();

        // includedRegionCodes is an array of up to 15 two-character country codes to restrict results to. (e.g., 'uk', 'fr').
        autocomplete.includedRegionCodes = ["ng"];

        // Clear and append to container;
        container.innerHTML = "";
        container.appendChild(autocomplete);

        // Store ref
        autocompleteRef.current = autocomplete;

        autocomplete.addEventListener(
          "gmp-select",
          async ({ placePrediction }) => {
            const place = placePrediction.toPlace();
            await place.fetchFields({
              fields: [
                "displayName",
                "formattedAddress",
                "location",
                "addressComponents",
              ],
            });

            // Send extracted data to parent
            onPlaceSelect(extract(place));
          }
        );

        // autocompleteRef.current = autocomplete;
      } catch (err) {
        console.error("Autocomplete init error:", err);
      }
    };

    init();

    return () => {
      if (autocomplete && container.contains(autocomplete)) {
        container.removeChild(autocomplete);
      }
      autocompleteRef.current = null;
    };
    // eslint-disable-next-line
  }, [isEditing, mapsLoaded, extract]);
  // }, [isEditing, extract, onPlaceSelect, selectedPlace]); // ← added selectedPlace

  // UI: Saved address + Change
  if (!isEditing && defaultAddress) {
    return (
      <div>
        <label className="block mb-2 text-sm font-medium text-[#151515] dark:text-white">
          {defaultAddressTitle ?? "Address"}
        </label>
        <div className="flex items-center justify-between sm:text-sm h-[40px] px-4 py-2 border border-gray-300 w-full text-base bg-[#FFFFFF] dark:bg-[transparent]">
          <span>{defaultAddress}</span>
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="text-primary text-sm font-medium hover:underline"
          >
            Change
          </button>
        </div>
      </div>
    );
  }

  // UI: Search box (Create OR Edit after Change)
  return (
    <div>
      <label className="block mb-2 text-sm font-medium text-[#151515] dark:text-white">
        {searchLocationTitle ?? "Search for a Location"}
      </label>

      <div
        ref={containerRef}
        className="sm:text-sm min-h-[40px] px-4 border border-gray-300 w-full text-base bg-[#FFFFFF] dark:bg-[transparent] focus:ring-2 focus:ring-red-600 focus:border-red-600 focus:outline-none"
      />

      {defaultAddress && isEditing && (
        <div className="flex justify-end mt-1">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="text-sm text-gray-500 hover:underline"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default LocationSearch;
